/*
 * Rendering the two kinds of date this application has, which are not the same
 * kind of thing. CLAUDE.md §4.
 *
 *   - A **calendar day** — a hire date, `customer_since`, a job's date. It means
 *     that day in Cedar City and has no time zone.
 *   - An **instant** — when a note was written, when a session started. A real
 *     moment, stored UTC, rendered in the reader's zone.
 *
 * Keeping both here makes the choice visible at the call site instead of
 * everything reaching for `new Date()`.
 */

/**
 * `2026-08-04` → `4 Aug 2026`.
 *
 * Lives in `@shared/dates` because the server writes calendar days into
 * notification bodies and emails too, and a date that reads one way on the
 * screen and another in the email about it is a support call. Re-exported here
 * so every page keeps importing its date formatting from one place.
 */
export { formatCalendarDay as formatDay } from '@shared/dates';

/**
 * An ISO instant → `4 Aug 2026, 14:32`.
 *
 * This one genuinely is a moment, so `new Date` is correct here and the
 * reader's own time zone is the right one to show it in.
 */
export function formatInstant(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * A wall-clock `HH:MM[:SS]` → `9:00 AM`. Moved to `@shared/dates` in 11.2 —
 * the server words job times into notifications now — and re-exported here so
 * pages keep one import for date formatting.
 */
export { formatWallTime } from '@shared/dates';

/**
 * Minutes → `2h` / `1h 30m` / `45m`.
 *
 * One formatter for every portal. P26 is the prototype showing `180 min` on
 * the employee side and `3h` on the client side for the same job.
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${String(rest)}m`;
  if (rest === 0) return `${String(hours)}h`;
  return `${String(hours)}h ${String(rest)}m`;
}
